import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Cloud, Zap, Clock, ShieldCheck, Globe, Users } from 'lucide-react';
import TopBar from '../../components/TopBar';
import PageTitle from '../../components/PageTitle';
import ContactFormModal from '../../components/ContactFormModal';
import SiteFooter from '../../components/SiteFooter';
import { fetchHomeGpuPricing, type HomeGpuPricingEntry } from '../../utils/pricing';

const CLOUD_URL = import.meta.env.VITE_CLOUD_URL || 'https://cloud.etherworks.cn';

const featureDefs = [
  { icon: Zap, key: 'performance', color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  { icon: Clock, key: 'onDemand', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  { icon: ShieldCheck, key: 'secure', color: 'text-green-500', bg: 'bg-green-500/10' },
  { icon: Globe, key: 'global', color: 'text-violet-500', bg: 'bg-violet-500/10' },
];

function formatPrice(price: number) {
  return `$${price.toFixed(2)}`;
}

export default function GpuCloud() {
  const { t } = useTranslation('products');
  const [showContact, setShowContact] = useState(false);
  const [enabledGpus, setEnabledGpus] = useState<HomeGpuPricingEntry[]>([]);

  useEffect(() => {
    const loadPricing = async () => {
      const gpus = await fetchHomeGpuPricing();
      setEnabledGpus(gpus.filter((gpu) => gpu.enable).sort((a, b) => b.securePrice - a.securePrice));
    };

    void loadPricing();
  }, []);

  const topGpus = enabledGpus.slice(0, 4);

  return (
    <div className="min-h-screen bg-background">
      <PageTitle titleKey="gpu_cloud.title" namespace="products" descriptionKey="gpu_cloud.subtitle" />
      <TopBar />

      {/* Hero */}
      <section className="relative pt-24 pb-8 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/[0.03] via-transparent to-transparent dark:from-cyan-500/[0.06]" />
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(6,182,212,0.07) 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="absolute top-16 left-[10%] w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-4 right-[8%] w-56 h-56 bg-sky-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-sky-600 items-center justify-center mb-6 shadow-lg shadow-cyan-500/20">
            <Cloud className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-4">
            <span className="bg-gradient-to-r from-cyan-500 via-sky-500 to-cyan-600 bg-clip-text text-transparent">
              {t('gpu_cloud.name')}
            </span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground font-medium mb-8 max-w-2xl mx-auto">{t('gpu_cloud.subtitle')}</p>

          <div className="flex justify-center flex-wrap gap-4 mb-10">
            <a
              href={`${CLOUD_URL}/console/compute/pods`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-cyan-500 to-sky-600 hover:from-cyan-600 hover:to-sky-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/30 hover:scale-[1.02] active:scale-[0.98]"
            >
              {t('gpu_cloud.cta')}
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/plans"
              className="inline-flex items-center gap-2 px-8 py-3.5 border border-input bg-background hover:bg-accent hover:text-accent-foreground font-semibold rounded-xl transition-all"
            >
              {t('gpu_cloud.view_pricing')}
            </Link>
          </div>

          {/* Stats */}
          <div className="flex justify-center gap-8 flex-wrap">
            {[
              { value: `${enabledGpus.length}+`, label: t('gpu_cloud.stat_gpus') },
              { value: '99.9%', label: t('gpu_cloud.stat_uptime') },
              { value: '24/7', label: t('gpu_cloud.stat_support') },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center">
                <span className="text-2xl font-bold text-foreground">{s.value}</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GPU Preview */}
      <section className="pt-8 pb-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('gpu_cloud.pricing_title')}</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">{t('gpu_cloud.pricing_subtitle')}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {topGpus.map((gpu) => (
              <div key={gpu.id} className="rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors">
                <h3 className="text-lg font-bold text-foreground mb-1">{gpu.displayName}</h3>
                <div className="text-xs text-muted-foreground space-y-1 mb-4">
                  <div>{t('gpu_cloud.vram')}: {gpu.memoryInGb} GB</div>
                </div>
                <div className="text-2xl font-bold text-primary">
                  {formatPrice(gpu.securePrice)}
                  <span className="text-sm font-normal text-muted-foreground">{t('gpu_cloud.per_hour')}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-8">
            <Link
              to="/plans"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              {t('gpu_cloud.view_full_pricing')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('gpu_cloud.features_title')}</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">{t('gpu_cloud.features_subtitle')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {featureDefs.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.key} className="group relative rounded-[1.25rem] border border-border p-2 md:p-3 transition-transform hover:scale-[1.02]">
                  <div className="relative flex h-full flex-col gap-3 overflow-hidden rounded-xl border border-border bg-background p-6 shadow-sm dark:shadow-[0px_0px_27px_0px_rgba(45,45,45,0.3)]">
                    <div className="flex items-center gap-3">
                      <div className={`h-9 w-9 rounded-lg ${f.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${f.color}`} />
                      </div>
                      <h3 className="text-base font-semibold text-foreground tracking-tight">{t(`gpu_cloud.feat_${f.key}_title`)}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(`gpu_cloud.feat_${f.key}_desc`)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-3xl mx-auto text-center">
          <Users className="w-10 h-10 text-primary mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3">{t('gpu_cloud.enterprise_title')}</h2>
          <p className="text-muted-foreground mb-8">{t('gpu_cloud.enterprise_subtitle')}</p>
          <button
            onClick={() => setShowContact(true)}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-cyan-500 to-sky-600 hover:from-cyan-600 hover:to-sky-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
          >
            {t('gpu_cloud.contact_sales')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <SiteFooter />

      {showContact && <ContactFormModal isOpen={showContact} onClose={() => setShowContact(false)} product="gpu-cloud" />}
    </div>
  );
}
